import { FieldPolicy } from '@apollo/client';
import { SocialProfileFieldsFragment } from '../models/graphql';
import { ReadFieldFunction } from '@apollo/client/cache/core/types/common';
import { MakeRequired } from '../models/basic-objects';

// Helper function for sorting based on displayName
type SocialProfileType = MakeRequired<SocialProfileFieldsFragment, '__typename'>['__typename'];

const possibleSortedSocialProfilesTypes = [
  'InstagramSocialProfile',
  'FacebookSocialProfile',
  'LinkedInSocialProfile',
  'TikTokSocialProfile',
  'TwitterSocialProfile',
  'PinterestSocialProfile',
  'GoogleSocialProfile',
  'GenericSocialProfile',
  'SocialProfile'
] as const;

// Adjusted utility type that works with readonly arrays
type EnsureAllSocialProfiles<T extends readonly string[]> = Exclude<SocialProfileType, T[number]> extends never
  ? Exclude<T[number], SocialProfileType> extends never
    ? true
    : never
  : never;

// This is just a TS helper that will puke a TS error if we don't include all possible SocialProfilesType in our
// possibleSortedSocialProfilesTypes const
const _ensureAllSocialProfilesIncluded: EnsureAllSocialProfiles<typeof possibleSortedSocialProfilesTypes> = true;
export const sortedSocialProfilesTypes = possibleSortedSocialProfilesTypes;

const sortProfiles = (
  a: SocialProfileFieldsFragment,
  b: SocialProfileFieldsFragment,
  readField: ReadFieldFunction
): -1 | 0 | 1 => {
  const typenameA = readField<SocialProfileFieldsFragment['__typename']>('__typename', a);
  const typenameB = readField<SocialProfileFieldsFragment['__typename']>('__typename', b);
  const displayNameA = readField<string>('displayName', a);
  const displayNameB = readField<string>('displayName', b);
  if (!typenameA || !typenameB) return 0;
  const indexA = sortedSocialProfilesTypes.indexOf(typenameA);
  const indexB = sortedSocialProfilesTypes.indexOf(typenameB);
  if (indexA < indexB) {
    return -1;
  } else if (indexA > indexB) {
    return 1;
  }
  return (displayNameA ?? '').localeCompare(displayNameB ?? '');
};

export const socialProfilesPolicy = (): FieldPolicy<SocialProfileFieldsFragment[], SocialProfileFieldsFragment[]> => {
  return {
    keyArgs: [],
    read(existing, { readField }) {
      if (!existing) {
        return undefined;
      }

      // Sort the profiles and return
      return Array.from(existing).sort((a, b) => sortProfiles(a, b, readField));
    }
  };
};
